// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    // XNG-Changes
    orderLabel?: TypographyPresentationProps;
    orderValue?: TypographyPresentationProps;
}

export const headerStyles: OnePageCheckoutConfirmationHeaderStyles = {
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h3",
    },
    orderInfoGridItem: {
        width: [8, 8, 10, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    orderNumberText: { variant: "h5" },
};

const styles = headerStyles;

const OnePageCheckoutConfirmationHeader = ({ cart, billTo }: Props) => {
    if (!cart || !billTo) {
        return null;
    }
    /**
     * XNG-Changes
     * BG-2729
     * Change Labels
     * Add sections for:
     *  1. purchase order number
     *  2. requested date
     *  3. pickup date
     *  4. order status
     *  5. order notes
     * Move print button out into order confirmation page container
     */
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleGridItem}>
                <Typography as="h3" {...styles.titleText}>
                    {translate("Order Details")}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.orderInfoGridItem}>
                <Typography {...styles.orderLabel} data-test-selector="orderConfirmation_orderNumberLabel" as="p">
                    {translate("Order Number")}
                    <Typography {...styles.orderValue} data-test-selector="orderConfirmation_orderNumberValue">
                        {cart.erpOrderNumber || cart.orderNumber}
                    </Typography>
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.orderInfoGridItem}>
                <Typography {...styles.orderLabel} data-test-selector="orderConfirmation_orderCartStatusLabel" as="p">
                    {translate("Order Status")}
                    <Typography {...styles.orderValue} data-test-selector="orderConfirmation_orderCartStatusValue">
                        {cart.status}
                    </Typography>
                </Typography>
            </StyledWrapper>{" "}
            <StyledWrapper {...styles.orderInfoGridItem}>
                <Typography {...styles.orderLabel} data-test-selector="orderConfirmation_orderPlaced" as="p">
                    {translate("Order Date")}
                    <Typography {...styles.orderValue}>
                        <LocalizedDateTime
                            dateTime={cart.orderDate}
                            options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                        />
                    </Typography>
                </Typography>
            </StyledWrapper>
            {cart.fulfillmentMethod === FulfillmentMethod.Ship && cart.requestedDeliveryDate && (
                <StyledWrapper {...styles.orderInfoGridItem}>
                    <Typography
                        {...styles.orderLabel}
                        data-test-selector="orderConfirmation_orderRequestedDateLabel"
                        as="p"
                    >
                        {translate("Request Date")}
                        <Typography {...styles.orderValue}>
                            <LocalizedDateTime
                                dateTime={cart.requestedDeliveryDateDisplay}
                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                            />
                        </Typography>
                    </Typography>
                </StyledWrapper>
            )}
            {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.requestedPickupDate && (
                <StyledWrapper {...styles.orderInfoGridItem}>
                    <Typography
                        {...styles.orderLabel}
                        data-test-selector="orderConfirmation_orderRequestedDateLabel"
                        as="p"
                    >
                        {translate("Pickup Date")}
                        <Typography {...styles.orderValue}>
                            <LocalizedDateTime
                                dateTime={cart.requestedPickupDateDisplay}
                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                            />
                        </Typography>
                    </Typography>
                </StyledWrapper>
            )}
            {cart.poNumber && (
                <StyledWrapper {...styles.orderInfoGridItem}>
                    <Typography {...styles.orderLabel} data-test-selector="orderConfirmation_orderPoNumberLabel" as="p">
                        {translate("Purchase Order")}
                        <Typography {...styles.orderValue} data-test-selector="orderConfirmation_orderPoNumberValue">
                            {cart.poNumber}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            )}
            {cart.notes && (
                <StyledWrapper {...styles.orderInfoGridItem}>
                    <Typography {...styles.orderLabel} data-test-selector="orderConfirmation_orderNotesLabel" as="p">
                        {translate("Order Notes")}
                        <Typography {...styles.orderValue} data-test-selector="orderConfirmation_orderNotesValue">
                            {cart.notes}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            )}
            {/* XNG-Change */}
            {/* OrderConfirmation_EmailSent message isn't showing on designs */}
            {/* <StyledWrapper width={12}>
                <Typography
                    as="p"
                    {...styles.emailSentText}
                    data-test-selector="orderConfirmation_EmailSent">
                    {siteMessage("OrderConfirmation_EmailSent", billTo.email)}
                </Typography>
            </StyledWrapper> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationHeader),
    definition: {
        displayName: "Header",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
